<template>
    <FilterBase :title>
        <ThButton
            v-for="value in bounds"
            :key="`${value.colour}-${value.crystal}`"
            v-qa="JSON.stringify(value)"
            type="solid"
            size="small"
            :disabled
            :selected="isSelected(value)"
            :class="getClasses(value)"
            @click="select(value)"
        >
            {{ getGemstoneTypeNameNew(value) }}
        </ThButton>
    </FilterBase>
</template>

<script setup lang="ts">
import type { GemsApi_GemstoneTypeFilter } from '@/graphql/api'
import {
    getGemstoneTypeNameNew,
    isSameGemstoneType,
} from '@/domain/stone/search/sku/gemstone'

import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'

interface Props {
    modelValue?: GemsApi_GemstoneTypeFilter[] | null
    bounds: GemsApi_GemstoneTypeFilter[]
    title?: string
    isMulti?: boolean
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    isMulti: true,
    title: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [GemsApi_GemstoneTypeFilter[]]
}>()

const isSelected = (value: GemsApi_GemstoneTypeFilter): boolean => {
    if (!props.modelValue) {
        return false
    }

    return props.modelValue?.includes(value)
}

const getClasses = (
    value: GemsApi_GemstoneTypeFilter
): Record<string, boolean> => ({
    button: true,
    selected: isSelected(value),
})

const updateModelValue = (value: GemsApi_GemstoneTypeFilter[]) => {
    emit('update:modelValue', value)
}

const select = (value: GemsApi_GemstoneTypeFilter) => {
    if (props.isMulti) {
        if (!props.modelValue) {
            updateModelValue([value])
        } else if (props.modelValue.some((f) => isSameGemstoneType(f, value))) {
            const model = props.modelValue.filter(
                (f) => !isSameGemstoneType(f, value)
            )

            updateModelValue(model)
        } else {
            const model = [...props.modelValue, value]

            updateModelValue(model)
        }
    } else {
        if (props.modelValue?.some((f) => isSameGemstoneType(f, value))) {
            updateModelValue([])
        } else {
            updateModelValue([value])
        }
    }
}
</script>

<style lang="scss" scoped>
:deep(.button) {
    margin: 0 10px 10px 0;

    &.small {
        padding: 7px 25px;
    }

    &.solid {
        background: transparent;
        color: $slate;
        border-color: $grey-300;

        &:hover {
            background: $grey-200;
            color: $slate;
        }
    }

    &.selected {
        border-color: $slate;
    }
}
</style>
